import { Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom'
import { useEffect } from 'react'

// Layout
import Layout from './components/layout/Layout'
import RoleBasedRedirect from './components/layout/RoleBasedRedirect'

// Toast Provider
import { ToastProvider } from './components/ui/Toast'

// Error Boundary and Offline Indicator
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary'
import { OfflineIndicator } from './components/ui/OfflineIndicator/OfflineIndicator'

// Pages
import HomePage from './pages/HomePage'
import SearchPage from './pages/search/SearchPage'
import SearchResultsPage from './pages/search/SearchResultsPage'
import ProfilePage from './pages/business/ProfilePage'
import CustomerProfilePage from './pages/customer/CustomerProfilePage'
import AnalyticsPage from './pages/business/AnalyticsPage'
import AdminLoginPage from './pages/auth/AdminLoginPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import FitmentCentresPage from './pages/business/FitmentCentresPage'
import InventoryPage from './pages/business/InventoryPage'
import RimInventoryPage from './pages/business/RimInventoryPage'
// DealsPage, BusinessSignUpPage, SellerSignUpPage, SellerSignUpProcessPage removed —
// shop self-registration and the deals feature are no longer offered on the site.
// Shops are added and managed via the admin shop-admin page instead.
import AdminBusinessApprovalPage from './pages/business/AdminBusinessApprovalPage'
import PlatformAdminDashboard from './pages/admin/PlatformAdminDashboard'
import SellerDashboard from './pages/business/SellerDashboard'
import ReviewManagementPage from './pages/business/ReviewManagementPage'
import ShopPage from './pages/business/ShopPage'
import BookmarksPage from './pages/bookmarks/BookmarksPage'
import ChatPage from './pages/ChatPage'

// Legal Pages
import TermsPage from './pages/legal/TermsPage'
import PrivacyPage from './pages/legal/PrivacyPage'
import PopiaPage from './pages/legal/PopiaPage'
import ContactPage from './pages/legal/ContactPage'
// CompetitionRulesPage removed — the R2500 competition has ended.
import { trackPageView } from './lib/tracking'

// Stores
import { useAuthStore } from './stores/authStore'

// Services
import { supabase } from './services/supabase'

// Gate for pages only app users (customers) may see — e.g. a shop's details
// and its stock. Not-logged-in visitors are sent to the chat, where they can
// register on WhatsApp. We wait for the session to finish loading first, so a
// logged-in user refreshing the page is never wrongly bounced.
const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { user, isLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !user) {
      navigate('/#chat', { replace: true });
    }
  }, [user, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  if (!user) return null;
  return <>{children}</>;
};

const RequireAdmin = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, isLoading } = useAuthStore();
  
  // Show loading while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Please Log In</h2>
          <p className="text-gray-600 mb-4">You need to log in to access the business dashboard.</p>
          <a href="/login" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Go to Login
          </a>
        </div>
      </div>
    );
  }
  
  if (profile?.role !== 'business') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">This area is restricted to business accounts only.</p>
          <a href="/" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }
  
  return <>{children}</>;
};

const RequirePlatformAdmin = ({ children }: { children: React.ReactNode }) => {
  const { user, profile } = useAuthStore();
  if (!user) return <div>Please log in as platform admin</div>;
  if (profile?.role !== 'admin') return <div>Access denied - Platform admin only</div>;
  return <>{children}</>;
};

const RoleBasedProfile = () => {
  const { profile } = useAuthStore();
  
  // Business users get the business profile page with sidebar
  if (profile?.role === 'business') {
    return <ProfilePage />;
  }
  
  // Customer users get the customer profile page without sidebar
  return <CustomerProfilePage />;
};


// Fires a PageView on every SPA route change. Without this, Meta and GA only
// ever see the first page load, and retargeting audiences stay empty.
function RouteTracker() {
  const location = useLocation()
  useEffect(() => {
    trackPageView(location.pathname)
  }, [location.pathname])
  return null
}

function App() {
  const { setUser, setLoading } = useAuthStore()

  useEffect(() => {
    // Get initial session
    const getInitialSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        setUser(session?.user ?? null)
      } catch (error) {
        console.error('Error getting initial session:', error)
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    getInitialSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null)
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [setUser, setLoading])

  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineIndicator />
        {/* <ChatBot /> */}
        <RouteTracker />
        <Routes>
          <Route path="/login" element={<Navigate to="/#chat" replace />} />
          {/* Shop self-registration removed — redirect any old links to home */}
          <Route path="/signup" element={<Navigate to="/" replace />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="admin-login" element={<AdminLoginPage />} />

          {/* Seller Sign Up Process removed — redirect to home */}
          <Route path="/seller_sign_up_process" element={<Navigate to="/" replace />} />
        
          {/* Legal Pages */}
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/popia" element={<PopiaPage />} />
          <Route path="/legal/contact" element={<ContactPage />} />
          {/* Competition has ended — redirect old links to home */}
          <Route path="/competition" element={<Navigate to="/" replace />} />
          <Route path="/legal/competition" element={<Navigate to="/" replace />} />
        
          {/* Platform Admin Routes - Outside of main layout */}
          <Route
            path="admin/platform"
            element={
              <RequirePlatformAdmin>
                <PlatformAdminDashboard />
              </RequirePlatformAdmin>
            }
          />
        
          {/* Seller Routes - Outside of main layout */}
          <Route
            path="/business/seller-dashboard"
            element={
              <RequireAdmin>
                <SellerDashboard />
              </RequireAdmin>
            }
          />
          <Route
            path="/business/inventory"
            element={
              <RequireAdmin>
                <InventoryPage />
              </RequireAdmin>
            }
          />
          <Route
            path="/business/rim-inventory"
            element={
              <RequireAdmin>
                <RimInventoryPage />
              </RequireAdmin>
            }
          />
          {/* Deals feature removed - route redirects rather than 404s if bookmarked/linked */}
          <Route path="/business/deals" element={<Navigate to="/" replace />} />
          {/* Bookings hidden for now - route redirects rather than 404s if bookmarked/linked */}
          <Route path="/business/bookings" element={<Navigate to="/" replace />} />
          <Route
            path="/business/profile"
            element={
              <RequireAdmin>
                <ProfilePage />
              </RequireAdmin>
            }
          />
          <Route
            path="/business/analytics"
            element={
              <RequireAdmin>
                <AnalyticsPage />
              </RequireAdmin>
            }
          />
          {/* Messages hidden for now - route redirects rather than 404s if bookmarked/linked */}
          <Route path="/business/messages" element={<Navigate to="/" replace />} />
          <Route
            path="/business/reviews"
            element={
              <RequireAdmin>
                <ReviewManagementPage />
              </RequireAdmin>
            }
          />
          
          {/* Main App Routes */}
          <Route path="/" element={
            <RoleBasedRedirect>
              <Layout />
            </RoleBasedRedirect>
          }>
            <Route index element={<HomePage />} />
            <Route path="search" element={<SearchPage />} />
            {/* Open browsing — registration is optional */}
            <Route path="search/results" element={<SearchResultsPage />} />
            <Route path="comparison" element={<div>Comparison feature coming soon</div>} />
            {/* Bookings and Messages hidden for now - routes redirect rather than 404 if bookmarked/linked */}
            <Route path="booking" element={<Navigate to="/" replace />} />
            <Route path="booking/:slug" element={<Navigate to="/" replace />} />
            <Route path="profile" element={
              <RequireAuth>
                <RoleBasedProfile />
              </RequireAuth>
            } />
            <Route path="messages" element={<Navigate to="/" replace />} />
            <Route path="bookmarks" element={
              <RequireAuth>
                <BookmarksPage />
              </RequireAuth>
            } />
            {/* Account-holder chat page. Anonymous visitors are bounced to the
                open home chat by RequireAuth — the main chat needs no login. */}
            <Route path="chat" element={
              <RequireAuth>
                <ChatPage />
              </RequireAuth>
            } />
            
            {/* Protect all /business routes */}
            <Route
              path="business"
              element={
                <RequireAdmin>
                  <SellerDashboard />
                </RequireAdmin>
              }
            />
            <Route
              path="business/profile"
              element={
                <RequireAdmin>
                  <ProfilePage />
                </RequireAdmin>
              }
            />
            <Route
              path="business/analytics"
              element={
                <RequireAdmin>
                  <AnalyticsPage />
                </RequireAdmin>
              }
            />
            {/* Bookings and Messages hidden for now - routes redirect rather than 404 if bookmarked/linked */}
            <Route path="business/messages" element={<Navigate to="/" replace />} />
            <Route path="business/bookings" element={<Navigate to="/" replace />} />
            <Route
              path="business/inventory"
              element={
                <RequireAdmin>
                  <InventoryPage />
                </RequireAdmin>
              }
            />
            {/* Deals feature and business self-signup removed - redirect to home */}
            <Route path="business/deals" element={<Navigate to="/" replace />} />
            <Route path="business/signup" element={<Navigate to="/" replace />} />
            {/* Legacy business admin route - kept for backward compatibility */}
            <Route
              path="admin/business-approval"
              element={
                <RequireAdmin>
                  <AdminBusinessApprovalPage />
                </RequireAdmin>
              }
            />
            
            <Route path="fitment-centres" element={<FitmentCentresPage />} />
            <Route path="business/fitment-centres" element={<FitmentCentresPage />} />
          {/* Shop Page Route — open browsing (registration is optional) */}
          <Route path="business/:slug" element={<ShopPage />} />
          </Route>
        </Routes>
      </ToastProvider>
    </ErrorBoundary>
  )
}

export default App
